/**
 * PathwayMenuModal.jsx — iter822 (Feb 2026)
 *
 * "🍽️ 7-дневно меню от Вашите активирани пътища"
 *
 * A modal that fetches POST /api/bhi/pathway-menu (deriving foods +
 * pathways from the patient's structured conditions) and renders a
 * scannable 7-day meal plan with per-meal evidence (which foods and
 * KEGG-aligned recipe tags each meal supports).
 *
 * Triggered from the PathwayExplorer "Генерирай меню" button.
 * Educational — not clinical prescription.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Loader2, ChefHat, Utensils, Clock, Flame, ChevronDown, ChevronRight,
  Sparkles, ShieldCheck, Save, Check, AlertTriangle, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/App";

const MEAL_LABELS = {
  breakfast: "Закуска",
  lunch:     "Обяд",
  dinner:    "Вечеря",
  snack:     "Междинно",
};

const MEAL_ICONS = {
  breakfast: "🥣", lunch: "🥗", dinner: "🍽️", snack: "🥜",
};

const TAG_LABELS_BG = {
  insulin_sensitive: "Инсулин-чувствит.",
  low_glycemic:      "Ниско-гликемично",
  anti_inflammatory: "Противовъзпалит.",
  omega_balanced:    "Омега-баланс",
  antioxidant:       "Антиоксидант",
  cardio_protective: "Кардиопротект.",
  gut_friendly:      "Чревно-здраво",
  satiety_high:      "Наситеност",
};

export default function PathwayMenuModal({ open, onClose, isDark = false, focusFood = null }) {
  const { API } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [expandedDay, setExpandedDay] = useState(1);
  const [saving, setSaving]   = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [saveErr, setSaveErr] = useState(null);

  const fetchMenu = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // iter834 — When `focusFood` is provided (from a BTRE food-chip
      // deep-link), pass it as the sole food filter so the backend
      // scores recipes matching THAT ingredient only. Pathways are still
      // auto-derived from the user's structured conditions.
      const body = focusFood ? { foods: [focusFood] } : {};
      const { data } = await axios.post(`${API}/bhi/pathway-menu`, body);
      setData(data);
      setSavedId(null); setSaveErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Неуспешно генериране.");
    } finally {
      setLoading(false);
    }
  }, [API, focusFood]);

  const savePlan = useCallback(async () => {
    if (!data) return;
    setSaving(true); setSaveErr(null);
    try {
      const { data: res } = await axios.post(`${API}/bhi/pathway-menu/save`, { menu: data });
      setSavedId(res.plan_id);
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e.message || "Неуспешно записване.");
    } finally {
      setSaving(false);
    }
  }, [API, data]);

  useEffect(() => {
    if (open && !data && !loading) fetchMenu();
  }, [open, data, loading, fetchMenu]);

  // iter834 — Reset cached menu when focus food changes so re-opening
  // with a different ingredient triggers a fresh fetch.
  useEffect(() => {
    setData(null);
  }, [focusFood]);

  if (!open) return null;

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)] text-slate-100"
    : "bg-white border-slate-200 text-slate-800";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="pathway-menu-modal"
      >
        <motion.div
          initial={{ y: 30, scale: 0.98, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          className={`${cardCls} border rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-slate-200 dark:border-slate-700 shrink-0">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shrink-0">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-extrabold bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
                  7-дневно меню от Вашите активирани пътища
                </h3>
                {focusFood ? (
                  <p className="text-xs mt-1 opacity-80 flex items-center gap-1.5 flex-wrap" data-testid="pathway-menu-focus-banner">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[11px]">
                      📖 Филтър: {focusFood}
                    </span>
                    <span className="opacity-70">— рецептите съдържат тази съставка + подкрепят Вашите активни KEGG пътища.</span>
                  </p>
                ) : (
                  <p className="text-xs mt-1 opacity-70">
                    Всяко ястие показва кои pathway-подкрепени храни съдържа и кои KEGG-съответстващи функционални тагове носи.
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 shrink-0"
              data-testid="pathway-menu-modal-close"
              aria-label="Затвори"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading && (
              <div className="py-12 flex flex-col items-center justify-center opacity-70" data-testid="pathway-menu-loading">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <span className="text-sm">Генериране на 7-дневно меню…</span>
              </div>
            )}

            {err && (
              <div className="p-4 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="pathway-menu-error">
                {err}
              </div>
            )}

            {!loading && !err && data && (
              <>
                {/* iter824 — Dietary drivers banner. Only shown when
                    patient has real NIS/DIL/UIS data (meal_log ≥ 5). */}
                {data.dietary_context?.boost_applied && (
                  <div
                    className={`mb-4 p-3 rounded-xl border ${isDark ? "bg-amber-500/10 border-amber-700" : "bg-amber-50 border-amber-300"}`}
                    data-testid="pathway-menu-dietary-banner"
                  >
                    <div className="flex items-start gap-2">
                      <TrendingUp className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-amber-300" : "text-amber-700"}`} />
                      <div className="text-[11px] leading-relaxed">
                        <b className={isDark ? "text-amber-200" : "text-amber-800"}>Хранителен модел (14 дни):</b>
                        {" "}NIS = <b>{data.dietary_context.nis_avg}</b> ·
                        {" "}DIL = <b>{data.dietary_context.dil_avg}</b>
                        {data.dietary_context.uis_avg > 0 && (<> · UIS = <b>{data.dietary_context.uis_avg}</b></>)}
                        <br />
                        <span className={isDark ? "text-amber-200/80" : "text-amber-800/80"}>
                          Приоритет за <i>insulin_sensitive / anti_inflammatory</i> рецепти
                          {data.dietary_context.nis_pressure > 0.2 ? " (висок NIS натиск)" : ""}
                          {data.dietary_context.dil_pressure > 0.2 ? " (висок DIL натиск)" : ""}.
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Meta strip — iter888: без kcal (за точна калорийна оценка → Симулатор на чинията) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4" data-testid="pathway-menu-meta">
                  <Meta label="Ястия" value={data.total_meals} icon={Utensils} accent="emerald" />
                  <Meta label="Използвани храни" value={data.foods_used?.length || 0} icon={Sparkles} accent="cyan" />
                  <Meta label="Използвани пътища" value={data.pathways_used?.length || 0} icon={ShieldCheck} accent="fuchsia" />
                </div>
                <a
                  href="/nutrition-lab"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 mb-4"
                  data-testid="link-plate-simulator"
                >
                  🍽 За точна калорийна и макро оценка → Симулатор на чинията
                </a>

                {/* Foods used tags */}
                <div className="mb-4">
                  <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">
                      Pathway-подкрепени храни, влезли в менюто
                    </div>
                    <div className="flex items-center gap-2">
                      {saveErr && (
                        <span className="text-[10px] text-red-500 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {saveErr}
                        </span>
                      )}
                      {savedId ? (
                        <span
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                          data-testid="pathway-menu-saved-indicator"
                        >
                          <Check className="w-3 h-3" /> Планът е записан
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={savePlan}
                          disabled={saving}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white hover:opacity-90 disabled:opacity-40 transition"
                          data-testid="pathway-menu-save-btn"
                        >
                          {saving ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Save className="w-3 h-3" />
                          )}
                          Запази това меню като план
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(data.foods_used || []).map((f, i) => (
                      <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Days */}
                <div className="space-y-2" data-testid="pathway-menu-days">
                  {(data.days || []).map((day) => (
                    <DayRow
                      key={day.day}
                      day={day}
                      isOpen={expandedDay === day.day}
                      onToggle={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
                      isDark={isDark}
                    />
                  ))}
                </div>

                <p className="mt-4 text-[10px] italic opacity-60">
                  {data.rationale_bg}
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Meta({ label, value, icon: Icon, accent }) {
  const clsMap = {
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    amber:   "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
    cyan:    "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300",
    fuchsia: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300",
  };
  return (
    <div className={`rounded-xl p-2.5 flex items-center gap-2 ${clsMap[accent]}`}>
      <Icon className="w-4 h-4" />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest">{label}</div>
        <div className="text-lg font-extrabold">{value}</div>
      </div>
    </div>
  );
}

function DayRow({ day, isOpen, onToggle, isDark }) {
  return (
    <div
      className={`rounded-lg border ${isDark ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white"}`}
      data-testid={`pathway-menu-day-${day.day}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-2 p-2.5"
        data-testid={`pathway-menu-day-toggle-${day.day}`}
      >
        {isOpen ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
        <span className="font-bold text-sm">Ден {day.day}</span>
        <span className="ml-auto text-[11px] opacity-70 flex items-center gap-2 flex-wrap justify-end">
          {day.total_matched_foods > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 font-bold">
              🥗 {day.total_matched_foods} храни
            </span>
          )}
          {day.total_matched_tags > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300 font-bold">
              🧬 {day.total_matched_tags} тагове
            </span>
          )}
        </span>
      </button>
      {isOpen && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
          {Object.entries(day.meals || {}).map(([slot, meal]) => (
            <MealCard key={slot} slot={slot} meal={meal} isDark={isDark} />
          ))}
        </div>
      )}
    </div>
  );
}

function MealCard({ slot, meal, isDark }) {
  return (
    <div
      className={`rounded-lg p-2.5 ${isDark ? "bg-slate-800/50" : "bg-slate-50"}`}
      data-testid={`pathway-menu-meal-${slot}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">
            {MEAL_ICONS[slot]} {MEAL_LABELS[slot] || slot}
          </div>
          <div className="text-sm font-semibold truncate">{meal.name_bg}</div>
        </div>
        <div className="text-[11px] opacity-70 flex flex-col items-end shrink-0">
          {meal.prep_min && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {meal.prep_min} мин
            </span>
          )}
        </div>
      </div>
      {(meal.matched_foods?.length > 0 || meal.matched_tags?.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {(meal.matched_foods || []).map((f, i) => (
            <span key={"f" + i} className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
              🥗 {f}
            </span>
          ))}
          {(meal.matched_tags || []).map((t, i) => (
            <span key={"t" + i} className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300">
              🧬 {TAG_LABELS_BG[t] || t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
